import { RouterAuthType } from '@/core/data/config/uiConfig.interface';
import { gambits } from '@/core/gambits';
import type { ApiError } from '@/core/network/http/httpClient.interface';
import { ref } from 'vue';

/** Mirrors the API unlock list. Premium renders a control only when it is listed. */
export const ProfileUnlock = {
  Handshake: 'handshake',
  Premium: 'premium',
} as const;

export type ProfileUnlock = (typeof ProfileUnlock)[keyof typeof ProfileUnlock];

export const PROFILE_HANDSHAKE_REQUIRED = 'handshake_required';

export const ProfileLockReason = {
  Profile: 'profile',
  Invite: 'invite',
} as const;

export type ProfileLockReason = (typeof ProfileLockReason)[keyof typeof ProfileLockReason];

const lockedProfile = ref<{
  username: string;
  unlocks: ProfileUnlock[];
  reason: ProfileLockReason;
} | null>(null);

export function useProfileLock() {
  return { lockedProfile };
}

export function openProfileLock(
  username: string,
  unlocks: ProfileUnlock[],
  reason: ProfileLockReason = ProfileLockReason.Profile,
) {
  lockedProfile.value = { username, unlocks, reason };
}

export function closeProfileLock() {
  lockedProfile.value = null;
}

/**
 * Premium checkout is not built. The button only renders once a denial lists
 * {@link ProfileUnlock.Premium}.
 */
export function openPremiumProfileUnlock() {}

export function isHandshakeRequired(error?: ApiError | null): boolean {
  return error?.errorKey === PROFILE_HANDSHAKE_REQUIRED;
}

export function profileUnlocks(error?: ApiError | null): ProfileUnlock[] {
  const listed = (error?.unlocks ?? []).filter(
    (unlock): unlock is ProfileUnlock => unlock === ProfileUnlock.Handshake || unlock === ProfileUnlock.Premium,
  );

  return listed.length ? listed : [ProfileUnlock.Handshake];
}

/**
 * A click inside the app has a previous screen to stay on. A cold load, a new
 * tab, and the hop through login are the profile URL itself, so they keep it.
 */
export function isInAppProfileVisit(fromName: string | symbol | null | undefined): boolean {
  if (fromName == null || fromName === '') return false;

  return !Object.values(RouterAuthType).includes(String(fromName) as RouterAuthType);
}

/** Where the locked page goes when it is dismissed. Auth is not a place to return to. */
export function lockedProfileExit(back: string | null | undefined): 'back' | 'sparks' {
  if (back && !back.startsWith('/auth')) return 'back';
  return 'sparks';
}

/**
 * In-app visits that are not allowed stop here and open the modal. Anything
 * else continues, and the profile route shows the locked page when needed.
 */
export async function denyInAppProfileVisit(input: {
  username: string;
  fromName: string | symbol | null | undefined;
  isAdmin: boolean;
  ownUsername?: string | null;
}): Promise<boolean> {
  if (!isInAppProfileVisit(input.fromName)) return false;
  if (input.isAdmin) return false;
  if (input.ownUsername && input.ownUsername === input.username) return false;

  const resolved = await gambits.userService.getUsername(input.username);
  if (resolved.error || !resolved.data?.user) return false;

  const profile = await gambits.userService.getUserByName(input.username);
  if (!isHandshakeRequired(profile.error)) return false;

  openProfileLock(input.username, profileUnlocks(profile.error));
  return true;
}
